import React from 'react';
import { useRestaurants, useDashboardKPIs } from '@/hooks/useRestaurants';

const TestSupabasePage: React.FC = () => {
  const { data: restaurants, isLoading, error } = useRestaurants();
  const { data: kpis } = useDashboardKPIs();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des données Supabase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-lg">
          <h2 className="text-red-800 font-bold text-xl mb-2">❌ Erreur de connexion</h2>
          <p className="text-red-700 mb-4">
            Impossible de se connecter à Supabase. Vérifie tes variables d'environnement.
          </p>
          <pre className="bg-red-100 p-3 rounded text-sm text-red-900 overflow-auto">
            {error instanceof Error ? error.message : 'Erreur inconnue'}
          </pre>
          <div className="mt-4 text-sm text-red-700">
            <p className="font-semibold mb-2">Vérifications :</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Le fichier <code>.env.local</code> existe ?</li>
              <li>Les variables VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY sont définies ?</li>
              <li>Tu as redémarré le serveur après avoir créé le .env.local ?</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            ✅ Connexion Supabase Réussie !
          </h1>
          <p className="text-gray-600">
            Les données sont chargées depuis la base de données PostgreSQL.
          </p>
        </div>

        {/* Restaurants */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            📋 Restaurants ({restaurants?.length || 0})
          </h2>
          <div className="space-y-4">
            {restaurants?.map((restaurant) => (
              <div
                key={restaurant.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {restaurant.name}
                    </h3>
                    <p className="text-gray-600 text-sm mt-1">
                      {restaurant.description}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <span className="text-gray-500">
                        📍 {restaurant.location}
                      </span>
                      <span className="text-gray-500">
                        💰 {restaurant.price_range}
                      </span>
                      <span className="text-gray-500">
                        🍽️ {restaurant.cuisine?.join(', ')}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        restaurant.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {restaurant.status}
                    </span>
                    {restaurant.pack && (
                      <span className="ml-2 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {restaurant.pack}
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex gap-6 text-xs text-gray-500">
                    <span>🔑 ID: {restaurant.id.substring(0, 8)}...</span>
                    <span>🔗 Slug: {restaurant.slug}</span>
                    <span>📧 {restaurant.email}</span>
                    <span>📞 {restaurant.phone}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* KPIs Dashboard */}
        {kpis && kpis.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              📊 Dashboard KPIs (Vue SQL)
            </h2>
            <div className="space-y-4">
              {kpis.map((kpi) => (
                <div
                  key={kpi.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {kpi.name}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded p-3">
                      <div className="text-xs text-gray-600 mb-1">
                        Visibilité Organique
                      </div>
                      <div className="text-2xl font-bold text-blue-600">
                        {kpi.organic_visibility || 0}
                      </div>
                    </div>
                    <div className="bg-green-50 rounded p-3">
                      <div className="text-xs text-gray-600 mb-1">
                        Backlinks
                      </div>
                      <div className="text-2xl font-bold text-green-600">
                        {kpi.backlinks || 0}
                      </div>
                    </div>
                    <div className="bg-yellow-50 rounded p-3">
                      <div className="text-xs text-gray-600 mb-1">Avis</div>
                      <div className="text-2xl font-bold text-yellow-600">
                        {kpi.review_count || 0}
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded p-3">
                      <div className="text-xs text-gray-600 mb-1">Note</div>
                      <div className="text-2xl font-bold text-purple-600">
                        {kpi.average_rating?.toFixed(1) || '0.0'}
                      </div>
                    </div>
                  </div>
                  {kpi.language_coverage && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="text-xs text-gray-600 mb-2">
                        Couverture Linguistique
                      </div>
                      <div className="flex gap-4">
                        <div className="text-sm">
                          🇫🇷 FR: <strong>{kpi.language_coverage.fr}%</strong>
                        </div>
                        <div className="text-sm">
                          🇬🇧 EN: <strong>{kpi.language_coverage.en}%</strong>
                        </div>
                        <div className="text-sm">
                          🇪🇸 ES: <strong>{kpi.language_coverage.es}%</strong>
                        </div>
                        <div className="text-sm">
                          🇵🇹 PT: <strong>{kpi.language_coverage.pt}%</strong>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Debug Info */}
        <div className="mt-6 bg-gray-100 rounded-lg p-4">
          <details className="cursor-pointer">
            <summary className="font-semibold text-gray-700">
              🔍 Debug Info (JSON)
            </summary>
            <pre className="mt-3 bg-white p-4 rounded overflow-auto text-xs">
              {JSON.stringify({ restaurants, kpis }, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
};

export default TestSupabasePage;

